import moment from 'moment'
import React, { useEffect, useState } from 'react'

import { EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiPanel, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import { AIPlanCampaignBuilder } from 'api/entities/AIPlanPreview'
import { IntervalUnit } from 'api/entities/MasterCampaignStub'
import { getIntervalUnitLabel } from 'utils/MasterCampaignPlanUtils'

import BackgroundContentCard from 'pages/MasterCampaignPlanPage/new/components/BackgroundContentCard'

interface AIPlanCampaignCardProps {
  builder: AIPlanCampaignBuilder
}

interface PostalCodeGroup {
  city: string
  postalCodes: { code: string; name: string }[]
}

export const AIPlanCampaignCard = ({ builder }: AIPlanCampaignCardProps) => {
  const [groupedPostalCodes, setGroupedPostalCodes] = useState<PostalCodeGroup[]>([])

  useEffect(() => {
    const grouped: PostalCodeGroup[] = []

    builder.zips?.forEach(target => {
      if (!target.name?.includes(',')) {
        return
      }

      const [, city] = target.name.split(',')
      let postalCodeGroup = grouped.find(g => g.city === city)
      if (!postalCodeGroup) {
        postalCodeGroup = { city, postalCodes: [] }
        grouped.push(postalCodeGroup)
      }
      postalCodeGroup.postalCodes.push(target)
    })

    setGroupedPostalCodes(grouped)
  }, [builder.zips])

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  const campaignTypeDisplay = builder.isOffAdCritter && builder.offAdCritterName ? builder.offAdCritterName : builder.campaignType
  const isGoogleCampaign = builder.isOffAdCritter && builder.offAdCritterName === 'Google'

  return (
    <div id={`campaign-${builder.id}`}>
      <EuiFlexGroup>
        <EuiFlexItem>
          <EuiPanel hasShadow={true} style={{ backgroundColor: '#ede7dd', padding: '3rem 5rem' }}>
            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiTitle size='s'>
                  <p>{builder.name}</p>
                </EuiTitle>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiSpacer size='m' />
            <EuiFlexGroup>
              <EuiFlexItem>
                <span>
                  <b>Campaign Type:</b> {campaignTypeDisplay}
                  <br />
                  <b>Starts:</b> {moment(builder.startDate).format('MMMM Do YYYY')}
                  <br />
                  <b>Duration:</b> {builder.intervalDuration} {getIntervalUnitLabel(builder.intervalUnit as IntervalUnit, builder.intervalDuration ?? 1)} {builder.intervalFrequency && builder.intervalFrequency > 1 ? `(every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit as IntervalUnit, builder.intervalFrequency)})` : ''}
                </span>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiFlexGroup>
              <EuiFlexItem>
                <span>
                  <b>Budget:</b> {formatter.format(builder.intervalBudget)}
                  {builder.isTotalBudget ? ' (total)' : (
                    <>
                      {builder.intervalFrequency && builder.intervalFrequency > 1 ? `/every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit as IntervalUnit, builder.intervalFrequency)}` : ''}
                      {builder.intervalFrequency && builder.intervalFrequency === 1 ? `/${getIntervalUnitLabel(builder.intervalUnit as IntervalUnit, builder.intervalFrequency)}` : ''}
                    </>
                  )}
                </span>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size='l' />

            {builder.longSummary && (
              <EuiFlexGroup>
                <EuiFlexItem>
                  <b>Summary</b>
                  <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                  <EuiSpacer size='s' />
                  <EuiText size='s'>
                    <p style={{ whiteSpace: 'pre-wrap' }}>{builder.longSummary}</p>
                  </EuiText>
                  <EuiSpacer size='l' />
                </EuiFlexItem>
              </EuiFlexGroup>
            )}

            <EuiSpacer size='l' />

            {builder.seasonalitySummary && (
              <EuiFlexGroup>
                <EuiFlexItem>
                  <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                    <b>Timing</b>
                    <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                    <EuiText size='s'>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{builder.seasonalitySummary}</p>
                    </EuiText>
                  </BackgroundContentCard>
                </EuiFlexItem>
              </EuiFlexGroup>
            )}

            <EuiFlexGroup alignItems='stretch'>
              {(builder.geoSummary || groupedPostalCodes.length > 0) && (
                <EuiFlexItem grow={4}>
                  <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                    <div>
                      <b>Geography</b>
                      <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                      {builder.geoSummary && (
                        <>
                          <EuiSpacer size='s' />
                          <EuiText size='s'>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{builder.geoSummary}</p>
                          </EuiText>
                        </>
                      )}
                    </div>
                    <EuiSpacer size='m' />
                    <ul style={{ marginLeft: '1rem' }}>
                      {groupedPostalCodes.map(({ city, postalCodes }, index) => (
                        <li key={index}>
                          <b>{city}</b>
                          <EuiSpacer size='xs' />
                          {postalCodes?.map((postalCode, pcIndex) => (
                            <ul key={pcIndex}>
                              <li>{postalCode.code}</li>
                            </ul>
                          ))}
                          <EuiSpacer size='s' />
                        </li>
                      ))}
                    </ul>
                  </BackgroundContentCard>
                </EuiFlexItem>
              )}
              {isGoogleCampaign && (builder.keywordDescription || (builder.keywords?.length ?? 0) > 0) && (
                <EuiFlexItem grow={6}>
                  <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                    <div>
                      <b>Keywords</b>
                      <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                      {builder.keywordDescription && (
                        <>
                          <EuiSpacer size='s' />
                          <EuiText size='s'>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{builder.keywordDescription}</p>
                          </EuiText>
                        </>
                      )}
                    </div>
                    <EuiSpacer size='m' />
                    {(builder.keywords?.length ?? 0) > 0 && (
                      <div>
                        <b>Sample Keywords</b>
                        <EuiSpacer size='s' />
                        {builder.keywords!.map((keyword, index) => (
                          <div key={index} style={{ marginBottom: '8px', display: 'flex', alignItems: 'flex-start' }}>
                            <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: '#343741', marginRight: '10px', marginTop: '7px', flexShrink: 0 }} />
                            <EuiText size='s'>{keyword}</EuiText>
                          </div>
                        ))}
                      </div>
                    )}
                  </BackgroundContentCard>
                </EuiFlexItem>
              )}
              {!isGoogleCampaign && (builder.audienceSummary || (builder.segmentTargets?.length ?? 0) > 0 || (builder.segmentDescriptions?.length ?? 0) > 0) && (
                <EuiFlexItem grow={6}>
                  <BackgroundContentCard style={{ backgroundColor: '#f5f5f5' }}>
                    <div>
                      <b>{builder.isOffAdCritter ? 'Audience' : 'Audience Segments'}</b>
                      <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                      {builder.audienceSummary && (
                        <>
                          <EuiSpacer size='s' />
                          <EuiText size='s'>
                            <p style={{ whiteSpace: 'pre-wrap' }}>{builder.audienceSummary}</p>
                          </EuiText>
                        </>
                      )}
                    </div>
                    <EuiSpacer size='m' />
                    <ul style={{ marginLeft: '1rem' }}>
                      {builder.isOffAdCritter ? (
                        builder.segmentDescriptions?.map((description, index) => (
                          <li key={index}>
                            <EuiText size='s'>{description}</EuiText>
                            <EuiSpacer size='s' />
                          </li>
                        ))
                      ) : (
                        builder.segmentTargets?.map((segment, index) => (
                          <li key={index}>
                            <b>{segment.hierarchy[segment.hierarchy.length - 1]}</b>
                            <div>
                              <EuiSpacer size='xs' />
                              {segment.name}
                              <EuiSpacer size='m' />
                            </div>
                          </li>
                        ))
                      )}
                    </ul>
                  </BackgroundContentCard>
                </EuiFlexItem>
              )}
            </EuiFlexGroup>
          </EuiPanel>
        </EuiFlexItem>
      </EuiFlexGroup>
    </div>
  )
}

export default AIPlanCampaignCard
